import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import { MediaUpload } from '@/components/MediaUpload';
import { Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blankSlide = () => ({
  background_image: '',
  background_image_mobile: '',
  foreground_image: '',
  heading_en: '',
  heading_hi: '',
  subheading_en: '',
  subheading_hi: '',
  bullets_en: [],
  bullets_hi: [],
  cta_primary_text_en: '',
  cta_primary_text_hi: '',
  cta_primary_link: '/projects',
  cta_secondary_text_en: '',
  cta_secondary_text_hi: '',
  cta_secondary_link: '#book-visit',
  is_active: true,
});

export default function HeroEditor() {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/site-settings`);
      const s = data;
      if (!Array.isArray(s.hero_slides) || !s.hero_slides.length) s.hero_slides = [blankSlide()];
      s.hero_slides = s.hero_slides.map((sl) => ({ ...blankSlide(), ...sl, bullets_en: sl.bullets_en || [], bullets_hi: sl.bullets_hi || [] }));
      setSettings(s);
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateSlide = (idx, patch) => {
    setSettings((s) => {
      const slides = [...s.hero_slides];
      slides[idx] = { ...slides[idx], ...patch };
      return { ...s, hero_slides: slides };
    });
  };
  const addSlide = () => setSettings((s) => ({ ...s, hero_slides: [...(s.hero_slides || []), blankSlide()] }));
  const removeSlide = (idx) => setSettings((s) => ({ ...s, hero_slides: s.hero_slides.filter((_, i) => i !== idx) }));
  const updateBullet = (idx, field, value, bIdx) => updateSlide(idx, { [field]: settings.hero_slides[idx][field].map((b, i) => (i === bIdx ? value : b)) });
  const addBullet = (idx, field) => updateSlide(idx, { [field]: [...settings.hero_slides[idx][field], ''] });
  const removeBullet = (idx, field, bIdx) => updateSlide(idx, { [field]: settings.hero_slides[idx][field].filter((_, i) => i !== bIdx) });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/site-settings`, settings, { headers });
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-brand-orange" /></div>;
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="hero-editor">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">Homepage Hero & Site Settings</h1>
          <p className="text-gray-500 text-sm mt-1">Edit the homepage hero, default share image and contact info.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-gradient-to-r from-brand-orange to-brand-rust text-white" data-testid="save-hero-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <div className="space-y-6">
        {settings.hero_slides.map((slide, idx) => (
          <Card key={idx}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Hero Slide {idx + 1}</CardTitle>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Switch checked={slide.is_active} onCheckedChange={(v) => updateSlide(idx, { is_active: v })} />
                  <span className="text-sm">Active</span>
                </div>
                {settings.hero_slides.length > 1 && (
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => removeSlide(idx)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Background image (desktop, ~1920×1080)</Label>
                  <MediaUpload kind="image" value={slide.background_image} onChange={(v) => updateSlide(idx, { background_image: v })} testId={`hero-bg-${idx}`} />
                </div>
                <div>
                  <Label className="mb-2 block">Background image (mobile, ~900×1200)</Label>
                  <MediaUpload kind="image" value={slide.background_image_mobile} onChange={(v) => updateSlide(idx, { background_image_mobile: v })} />
                </div>
                <div className="md:col-span-2">
                  <Label className="mb-2 block">Foreground / featured image (optional)</Label>
                  <MediaUpload kind="image" value={slide.foreground_image} onChange={(v) => updateSlide(idx, { foreground_image: v })} />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Heading (English)</Label>
                  <Input value={slide.heading_en} onChange={(e) => updateSlide(idx, { heading_en: e.target.value })} data-testid={`hero-heading-en-${idx}`} />
                </div>
                <div className="space-y-2">
                  <Label>Heading (Hindi)</Label>
                  <Input value={slide.heading_hi} onChange={(e) => updateSlide(idx, { heading_hi: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Subheading (English)</Label>
                  <Textarea rows={2} value={slide.subheading_en} onChange={(e) => updateSlide(idx, { subheading_en: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Subheading (Hindi)</Label>
                  <Textarea rows={2} value={slide.subheading_hi} onChange={(e) => updateSlide(idx, { subheading_hi: e.target.value })} />
                </div>
              </div>

              {/* Bullets */}
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { lang: 'en', field: 'bullets_en', label: 'Bullet points (English)' },
                  { lang: 'hi', field: 'bullets_hi', label: 'Bullet points (Hindi)' },
                ].map(({ field, label }) => (
                  <div key={field} className="space-y-2">
                    <Label>{label}</Label>
                    {(slide[field] || []).map((b, bIdx) => (
                      <div key={bIdx} className="flex items-center gap-2">
                        <Input value={b} onChange={(e) => updateBullet(idx, field, e.target.value, bIdx)} />
                        <Button size="icon" variant="outline" type="button" className="text-red-600" onClick={() => removeBullet(idx, field, bIdx)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" type="button" onClick={() => addBullet(idx, field)}>
                      <Plus className="h-4 w-4 mr-1" /> Add bullet
                    </Button>
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2 border rounded-lg p-3">
                  <Label className="font-semibold">Primary CTA</Label>
                  <Input placeholder="Text (English)" value={slide.cta_primary_text_en} onChange={(e) => updateSlide(idx, { cta_primary_text_en: e.target.value })} />
                  <Input placeholder="Text (Hindi)" value={slide.cta_primary_text_hi} onChange={(e) => updateSlide(idx, { cta_primary_text_hi: e.target.value })} />
                  <Input placeholder="Link (e.g. /projects)" value={slide.cta_primary_link} onChange={(e) => updateSlide(idx, { cta_primary_link: e.target.value })} />
                </div>
                <div className="space-y-2 border rounded-lg p-3">
                  <Label className="font-semibold">Secondary CTA</Label>
                  <Input placeholder="Text (English)" value={slide.cta_secondary_text_en} onChange={(e) => updateSlide(idx, { cta_secondary_text_en: e.target.value })} />
                  <Input placeholder="Text (Hindi)" value={slide.cta_secondary_text_hi} onChange={(e) => updateSlide(idx, { cta_secondary_text_hi: e.target.value })} />
                  <Input placeholder="Link or #book-visit" value={slide.cta_secondary_link} onChange={(e) => updateSlide(idx, { cta_secondary_link: e.target.value })} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        <Button variant="outline" onClick={addSlide} type="button">
          <Plus className="h-4 w-4 mr-2" /> Add another slide
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Default SEO & Share Image</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Site Title (English)</Label>
              <Input value={settings.site_title_en || ''} onChange={(e) => setSettings((s) => ({ ...s, site_title_en: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Site Title (Hindi)</Label>
              <Input value={settings.site_title_hi || ''} onChange={(e) => setSettings((s) => ({ ...s, site_title_hi: e.target.value }))} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Site Description (English)</Label>
              <Textarea rows={2} value={settings.site_description_en || ''} onChange={(e) => setSettings((s) => ({ ...s, site_description_en: e.target.value }))} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Site Description (Hindi)</Label>
              <Textarea rows={2} value={settings.site_description_hi || ''} onChange={(e) => setSettings((s) => ({ ...s, site_description_hi: e.target.value }))} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Site Keywords</Label>
              <Input value={settings.site_keywords || ''} onChange={(e) => setSettings((s) => ({ ...s, site_keywords: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Default Share/OG Image (1200×630)</Label>
            <MediaUpload kind="image" value={settings.default_share_image} onChange={(v) => setSettings((s) => ({ ...s, default_share_image: v }))} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Contact</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Phone</Label>
            <Input value={settings.contact_phone || ''} onChange={(e) => setSettings((s) => ({ ...s, contact_phone: e.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label>WhatsApp (digits only)</Label>
            <Input value={settings.contact_whatsapp || ''} onChange={(e) => setSettings((s) => ({ ...s, contact_whatsapp: e.target.value }))} placeholder="919131709809" />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input value={settings.contact_email || ''} onChange={(e) => setSettings((s) => ({ ...s, contact_email: e.target.value }))} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
